import { DEFAULT_POLYGON_PROTECTION_AREA_THRESHOLD } from "@buildwithflux/models";
import { enforceGridPrecisionInMeters, metersToMils } from "@buildwithflux/shared";
import { findWidthAndSpacing } from "../../../document";
import { getLayerIndex, roundUpToNearestPreferredTraceWidth } from "./helpers";
import { calculateResistanceFromEStandardValuesArray, e12StandardValuesInOhms, e192StandardValuesInOhms, e24StandardValuesInOhms, e48StandardValuesInOhms, e6StandardValuesInOhms, e96StandardValuesInOhms, nearestValueFromArray, standardCapacitance, } from "./helpers/calculateResistanceFromEStandardValuesArray";
import { calculateTraceWidth } from "./helpers/calculateTraceWidth";
import { getImpedanceControlProperties } from "./helpers/impedanceControl";
export function generalExpressionScopeGlobals(_context) {
    const globalsContextDeps = {};
    const globals = {
        findComponentByDesignator: (_designator) => {
            return;
        },
        findPcbNodeByDesignator: (_designator) => {
            return;
        },
    };
    return { globals, globalsContextDeps };
}
export function electricalExpressionScopeGlobals(_context) {
    const globalsContextDeps = {};
    const globals = {
        ohmsLaw: (voltageSource, voltageDrop, current) => {
            return (voltageSource - voltageDrop) / current;
        },
        ledBallast: (ledPowerRatingInAmps, ...args) => {
            // implements https://eepower.com/resistor-guide/resistor-applications/resistor-for-led/#
            return args.reduce((a, b) => a - b) / ledPowerRatingInAmps;
        },
        toE6: (value) => {
            return calculateResistanceFromEStandardValuesArray(e6StandardValuesInOhms, value);
        },
        toE12: (value) => {
            return calculateResistanceFromEStandardValuesArray(e12StandardValuesInOhms, value);
        },
        toE24: (value) => {
            return calculateResistanceFromEStandardValuesArray(e24StandardValuesInOhms, value);
        },
        toE48: (value) => {
            return calculateResistanceFromEStandardValuesArray(e48StandardValuesInOhms, value);
        },
        toE96: (value) => {
            return calculateResistanceFromEStandardValuesArray(e96StandardValuesInOhms, value);
        },
        toE192: (value) => {
            return calculateResistanceFromEStandardValuesArray(e192StandardValuesInOhms, value);
        },
        toStandardCapacitance: (value) => {
            // Not sure if this needs to use calculateNearestFromArray(..)
            return nearestValueFromArray(standardCapacitance, value);
        },
    };
    return { globals, globalsContextDeps };
}
export function mechanicalExpressionScopeGlobals(context) {
    const globalsContextDeps = {
        calculateTraceWidth: [
            "this.current",
            "this.desiredTemperatureRise",
            "this.bakedRules.layer",
            "this.bakedRules.preferredTraceWidths",
            "layout.bakedRules.stackup",
        ],
        calculateImpedanceTraceWidth: [
            "this.bakedRules.layer",
            "this.impedance",
            "this.impedancePair",
            "layout.bakedRules.stackup",
        ],
        calculateImpedanceTraceKeepout: [
            "this.bakedRules.layer",
            "this.impedance",
            "this.impedancePair",
            "layout.bakedRules.stackup",
        ],
        shouldProtectPolygon: ["this.area", "layout.area"],
    };
    const globals = {
        calculateTraceWidth: (currentInAmps, desiredTemperatureRiseInCelsius) => {
            const current = currentInAmps ?? Number(context.this?.current);
            if (!current) {
                return;
            }
            const currentLayer = context.this?.bakedRules?.layer;
            const preferredTraceWidths = context.this?.bakedRules?.preferredTraceWidths;
            const stackup = context.layout?.bakedRules?.stackup;
            const stackupLayers = Object.values(stackup ?? {});
            if (!stackup) {
                return;
            }
            if (!currentLayer) {
                return;
            }
            if (!preferredTraceWidths) {
                return;
            }
            let layer = "internal";
            if (currentLayer === "Top" || currentLayer === "Bottom") {
                layer = "external";
            }
            const layerIndex = getLayerIndex(stackupLayers, currentLayer);
            if (layerIndex < 0) {
                return;
            }
            const copperLayer = stackupLayers[layerIndex];
            if (!copperLayer) {
                return;
            }
            const traceThicknessInMils = metersToMils(copperLayer.thickness);
            const desiredTemperatureRise = desiredTemperatureRiseInCelsius
                ? Math.round(desiredTemperatureRiseInCelsius)
                : Number(context.this?.desiredTemperatureRise);
            const traceWidthInMeters = calculateTraceWidth(current, traceThicknessInMils, layer, desiredTemperatureRise);
            return enforceGridPrecisionInMeters(roundUpToNearestPreferredTraceWidth(preferredTraceWidths, traceWidthInMeters));
        },
        calculateImpedanceTraceWidth: (impedance, type) => {
            const impedanceProps = getImpedanceControlProperties(context, type);
            if (!impedanceProps) {
                return;
            }
            const impedanceInOhms = impedance ? Math.round(impedance) : Number(context.this?.impedance);
            if (!impedanceInOhms) {
                return;
            }
            const { width } = findWidthAndSpacing(Math.round(impedanceInOhms), impedanceProps);
            return enforceGridPrecisionInMeters(width);
        },
        calculateImpedanceTraceKeepout: (impedance, type) => {
            const impedanceProps = getImpedanceControlProperties(context, type);
            if (!impedanceProps) {
                return;
            }
            const impedanceInOhms = impedance ? Math.round(impedance) : Number(context.this?.impedance);
            if (!impedanceInOhms) {
                return;
            }
            const { spacing } = findWidthAndSpacing(Math.round(impedanceInOhms), impedanceProps);
            return enforceGridPrecisionInMeters(spacing);
        },
        /**
         * Determines if a polygon should be automatically protected during auto-layout.
         * Returns true if the polygon's area is less than 10% of the total layout area.
         * This prevents auto-layout from modifying small elements like logos or test points.
         */
        shouldProtectPolygon: (threshold = DEFAULT_POLYGON_PROTECTION_AREA_THRESHOLD) => {
            const layoutArea = context.layout?.area;
            const thisArea = context.this?.area;
            if (layoutArea === undefined || thisArea === undefined || layoutArea === 0) {
                return false;
            }
            const ratio = thisArea / layoutArea;
            return ratio < threshold;
        },
    };
    return { globals, globalsContextDeps };
}
const CACHED_ELECTRICAL_GLOBALS = electricalExpressionScopeGlobals({}).globals;
const CACHED_GENERAL_GLOBALS = generalExpressionScopeGlobals({}).globals;
export function createElectricalCustomScope(context) {
    const scope = {};
    Object.assign(scope, context);
    Object.assign(scope, CACHED_ELECTRICAL_GLOBALS);
    Object.assign(scope, CACHED_GENERAL_GLOBALS);
    return scope;
}
export function createMechanicalCustomScope(context) {
    const scope = {};
    Object.assign(scope, context);
    Object.assign(scope, mechanicalExpressionScopeGlobals(context).globals);
    Object.assign(scope, CACHED_GENERAL_GLOBALS);
    return scope;
}
//# sourceMappingURL=ExpressionContextSystemFunctions.js.map